'use client';
import React, { useEffect, useState } from 'react';
import {
  Building2, Users, CreditCard, AlertTriangle,
  Activity, Flag, Shield, TrendingUp, TrendingDown
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface KPICard {
  id: string;
  title: string;
  value: string;
  subValue?: string;
  trend?: { direction: 'up' | 'down' | 'flat'; value: string; label: string };
  status: 'healthy' | 'warning' | 'critical' | 'info' | 'neutral';
  icon: React.ElementType;
  span?: 'single' | 'double';
  description?: string;
}

interface DashboardStats {
  kpis: {
    activeOrgs: number;
    pendingSetup: number;
    licensedUsers: number;
    mrr: number;
    policyViolations: number;
    apiUptime: number;
    activeFeatureFlags: number;
    securityScore: number;
    suspendedOrgs: number;
    activeDeveloperSessions: number;
  };
}

const statusConfig = {
  healthy: {
    bg: 'bg-success/5',
    border: 'border-success/20',
    iconBg: 'bg-success/10',
    iconColor: 'text-success',
    valuColor: 'text-foreground',
    dot: 'bg-success',
  },
  warning: {
    bg: 'bg-warning/5',
    border: 'border-warning/30',
    iconBg: 'bg-warning/10',
    iconColor: 'text-warning',
    valuColor: 'text-foreground',
    dot: 'bg-warning',
  },
  critical: {
    bg: 'bg-danger/5',
    border: 'border-danger/30',
    iconBg: 'bg-danger/10',
    iconColor: 'text-danger',
    valuColor: 'text-danger',
    dot: 'bg-danger',
  },
  info: {
    bg: 'bg-info/5',
    border: 'border-info/20',
    iconBg: 'bg-info/10',
    iconColor: 'text-info',
    valuColor: 'text-foreground',
    dot: 'bg-info',
  },
  neutral: {
    bg: 'bg-muted',
    border: 'border-border',
    iconBg: 'bg-secondary',
    iconColor: 'text-muted-foreground',
    valuColor: 'text-foreground',
    dot: 'bg-muted-foreground',
  },
};

function formatMRR(value: number): string {
  if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
  if (value >= 1000) return `₹${(value / 1000).toFixed(1)}K`;
  return `₹${value}`;
}

export default function KPIBentoGrid() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState('');
  const [lastUpdatedDate, setLastUpdatedDate] = useState('');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch((process.env.NEXT_PUBLIC_API_URL || '') + '/api/dashboard/stats');
        if (res.ok) {
          const data = await res.json();
          setStats(data);
        }
      } catch (err) {
        console.error('Failed to fetch dashboard stats:', err);
      } finally {
        setLoading(false);
        const now = new Date();
        setLastUpdated(now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false }));
        setLastUpdatedDate(now.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }));
      }
    };

    fetchStats();
    const interval = setInterval(fetchStats, 60000); // refresh every minute
    return () => clearInterval(interval);
  }, []);

  const kpis = stats?.kpis;

  const kpiData: KPICard[] = [
    {
      id: 'kpi-active-orgs',
      title: 'Active Organizations',
      value: loading ? '—' : String(kpis?.activeOrgs ?? 0),
      subValue: loading ? '' : `${kpis?.pendingSetup ?? 0} pending setup`,
      trend: { direction: 'up', value: '+8', label: 'this month' },
      status: 'healthy',
      icon: Building2,
      span: 'double',
      description: 'Live tenants on platform',
    },
    {
      id: 'kpi-licensed-users',
      title: 'Licensed App Users',
      value: loading ? '—' : (kpis?.licensedUsers ?? 0).toLocaleString('en-IN'),
      subValue: 'across all orgs',
      trend: { direction: 'up', value: '+124', label: 'this week' },
      status: 'healthy',
      icon: Users,
    },
    {
      id: 'kpi-mrr',
      title: 'Platform MRR',
      value: loading ? '—' : formatMRR(kpis?.mrr ?? 0),
      subValue: 'Monthly recurring',
      trend: { direction: 'up', value: '+12.3%', label: 'vs last month' },
      status: 'healthy',
      icon: CreditCard,
    },
    {
      id: 'kpi-policy-violations',
      title: 'Policy Violations',
      value: loading ? '—' : String(kpis?.policyViolations ?? 0),
      subValue: 'Require attention',
      trend: { direction: 'up', value: '+3', label: 'since yesterday' },
      status: (kpis?.policyViolations ?? 0) > 0 ? 'critical' : 'healthy',
      icon: AlertTriangle,
      description: 'Require immediate attention',
    },
    {
      id: 'kpi-api-uptime',
      title: 'API Uptime (30d)',
      value: loading ? '—' : `${kpis?.apiUptime ?? 0}%`,
      subValue: '26 min downtime',
      trend: { direction: 'flat', value: '±0.01%', label: 'vs last period' },
      status: 'healthy',
      icon: Activity,
    },
    {
      id: 'kpi-feature-flags',
      title: 'Active Feature Flags',
      value: loading ? '—' : String(kpis?.activeFeatureFlags ?? 0),
      subValue: '3 overrides pending',
      trend: { direction: 'up', value: '+2', label: 'this week' },
      status: 'warning',
      icon: Flag,
    },
    {
      id: 'kpi-security-score',
      title: 'Security Score',
      value: loading ? '—' : `${kpis?.securityScore ?? 0}/100`,
      subValue: 'Good — 2 advisories',
      trend: { direction: 'down', value: '-4pts', label: 'vs last audit' },
      status: 'warning',
      icon: Shield,
    },
    {
      id: 'kpi-suspended-orgs',
      title: 'Suspended Orgs',
      value: loading ? '—' : String(kpis?.suspendedOrgs ?? 0),
      subValue: '2 payment overdue',
      trend: { direction: 'up', value: '+1', label: 'today' },
      status: (kpis?.suspendedOrgs ?? 0) > 0 ? 'critical' : 'healthy',
      icon: TrendingDown,
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-600 text-foreground">Platform Overview</h2>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-success pulse-dot" />
          <span className="text-[12px] text-muted-foreground">
            {loading ? 'Loading...' : `Live data · ${lastUpdatedDate}, ${lastUpdated} IST`}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
        {kpiData.map((card) => {
          const cfg = statusConfig[card.status];
          const Icon = card.icon;
          const isDouble = card.span === 'double';
          const TrendIcon = card.trend?.direction === 'up' ? TrendingUp : card.trend?.direction === 'down' ? TrendingDown : null;

          return (
            <div
              key={card.id}
              className={`
                card-hover rounded-xl border p-5 cursor-default
                ${cfg.bg} ${cfg.border}
                ${isDouble ? 'sm:col-span-2 lg:col-span-2' : ''}
                ${loading ? 'animate-pulse' : ''}
              `}
            >
              <div className="flex items-start justify-between mb-3">
                <div className={`p-2 rounded-lg ${cfg.iconBg}`}>
                  <Icon size={18} className={cfg.iconColor} />
                </div>
                {card.status === 'critical' && (
                  <span className="text-[10px] font-700 badge-danger px-2 py-0.5 rounded-full uppercase tracking-wide">Alert</span>
                )}
                {card.status === 'warning' && (
                  <span className="text-[10px] font-700 badge-warning px-2 py-0.5 rounded-full uppercase tracking-wide">Watch</span>
                )}
              </div>

              <div className="space-y-1">
                <p className="text-[12px] font-500 text-muted-foreground uppercase tracking-wide leading-none">{card.title}</p>
                <p className={`font-tabular font-700 leading-tight ${isDouble ? 'text-4xl' : 'text-2xl'} ${cfg.valuColor}`}>
                  {card.value}
                </p>
                {card.subValue && (
                  <p className="text-[12px] text-muted-foreground">{card.subValue}</p>
                )}
                {card.description && (
                  <p className="text-[11px] text-muted-foreground italic">{card.description}</p>
                )}
              </div>

              {card.trend && (
                <div className="mt-3 flex items-center gap-1.5">
                  {TrendIcon && (
                    <TrendIcon
                      size={12}
                      className={
                        card.trend.direction === 'up'
                          ? card.status === 'critical' ? 'text-danger' : 'text-success'
                          : card.trend.direction === 'down' ? 'text-danger' : 'text-muted-foreground'
                      }
                    />
                  )}
                  <span className={`text-[11px] font-600 font-tabular ${
                    card.trend.direction === 'up'
                      ? card.status === 'critical' ? 'text-danger' : 'text-success'
                      : card.trend.direction === 'down' ? 'text-danger' : 'text-muted-foreground'
                  }`}>
                    {card.trend.value}
                  </span>
                  <span className="text-[11px] text-muted-foreground">{card.trend.label}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}